import React, { Suspense, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FloatingCommentButton } from '@contently/toolkit';
import useTextEditorAnnotations from '../../hooks/useTextEditorAnnotations';
import FloatingCommentForm from './FloatingCommentForm';
import useOutsideClick from '../../hooks/useOutsideClick';
import TextEditorAnnotationComment from './TextEditorAnnotationComment';
import useStoryContext from '../../hooks/useStoryContext';
import AnnotationErrorBoundary from './AnnotationErrorBoundary';

export default function TextEditorAnnotations({
  consumerApi,
  contentId,
  allContentIds,
  positionContainerRef,
  storyId
}) {
  const {
    showCommentButton,
    setShowCommentButton,
    showCommentForm,
    activeAnnotation,
    setActiveAnnotation,
    yPos,
    handleNewAnnotation,
    handleRemoveAnnotation,
    handleSubmitComment,
    handleCancelComment
  } = useTextEditorAnnotations(consumerApi, contentId, allContentIds, positionContainerRef);

  // Get filter state directly from Redux store
  const { filterState } = useSelector(state => state.textEditor) || { filterState: 'All comments' };

  const { mentionableUsersBasicInfo } = useStoryContext();

  const hideComment = () => {
    setActiveAnnotation(null);
    handleCancelComment();
  };

  // Handle orphaned annotations (marks exist but annotation record is missing)
  // Note: Currently not called - the error boundary shows toast and inline error message
  // Keeping this for potential future use if we need additional cleanup logic
  const handleOrphanedAnnotation = (markId) => {
    console.warn(`Orphaned annotation detected: ${markId}. Error shown in comment panel.`);
  };

  const commentButtonRef = useRef(null);
  useOutsideClick(commentButtonRef, () => setShowCommentButton(false));

  return (
    <React.Fragment>
      {showCommentButton && (
        <div ref={commentButtonRef} className="floating-container-wrapper">
          <FloatingCommentButton
            top={yPos}
            onClick={handleNewAnnotation}
            data-testid="floating-comment-button"
          />
        </div>
      )}
      {showCommentForm && (
        <FloatingCommentForm
          yPos={yPos}
          handleSubmitComment={handleSubmitComment}
          handleCancelComment={handleCancelComment}
          users={mentionableUsersBasicInfo}
        />
      )}
      {activeAnnotation && (
        <Suspense fallback={null}>
          <div className="floating-container-wrapper">
            <AnnotationErrorBoundary
              markId={activeAnnotation}
              onOrphanedAnnotation={handleOrphanedAnnotation}
            >
              <TextEditorAnnotationComment
                allContentIds={allContentIds}
                activeAnnotation={activeAnnotation}
                handleRemoveAnnotation={handleRemoveAnnotation}
                yPos={yPos}
                hide={hideComment}
                storyId={storyId}
                filterState={filterState}
              />
            </AnnotationErrorBoundary>
          </div>
        </Suspense>
      )}
    </React.Fragment>
  );
}

TextEditorAnnotations.propTypes = {
  consumerApi: PropTypes.shape({}).isRequired,
  contentId: PropTypes.number.isRequired,
  allContentIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  positionContainerRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired,
  storyId: PropTypes.number.isRequired
};



// WEBPACK FOOTER //
// ./app/javascript/client/components/textEditor/TextEditorAnnotations.jsx